import { TipoPagamento } from "@refere/ui-utils/ui/components/FormDadosPagamento";

import formatISO from "date-fns/formatISO";

import { router } from "../../../App";
import { globalState } from "../../global-state";
import { enumPasso } from "../../simulacao";
import { fetchWrapper } from "../../../utils/fetch";

export interface IContaCorrente {
    codBanco?: string
    nomeBanco?: string
    agencia?: string
    digitoAgencia?: string
    conta?: string
    digitoConta?: string
}

export interface IPageCertificadoState {
    dtHrImpressaoProposta?: string
    dtHrPropostaAnexada?: string

    propostasAnexadas?: {
        idAnexo?: number
        dtHrAnexo?: string
        nomeArquivo?: string
    }[]

    dtHrPagamento?: string
    ehEfetivouSigas?: boolean
    ehRenovacao?: boolean
    urlCheckout?: string
    statusPagamento?: string

    dtHrEmissaoCertificado?: string

    dtHrCancelamento?: string
    codMotivoCancelamento?: string
    nomeArquivoFormCancelamento?: string
    motivosCancelamento?: {
        codMotivo?: string
        descricao?: string
        usarFormCancelamento?: boolean
    }[]

    codTipoPagamento?: number
    codBanco?: string
    agencia?: string
    digitoAgencia?: string
    conta?: string
    digitoConta?: string
    nomeBanco?: string
    dtHrDefinicaoDadosBancariosDBC?: string

    tiposPagamento?: TipoPagamento[]
}

const getPageCertificado = () =>
    fetchWrapper<IPageCertificadoState>("/api/certificado")

const getContasCorrentesAilosFetch = () =>
    fetchWrapper<IContaCorrente[]>("/api/Certificado/ailos/contas-correntes")

export const getContasCorrentesAilos = async () => {
    globalState.isLoading.set(true)

    const response = await getContasCorrentesAilosFetch()

    globalState.isLoading.set(false)

    return response ?? []
}

export const downloadPdfProposta = async () => {
    globalState.isLoading.set(true);

    const response = await fetchWrapper(
        "/api/certificado/proposta/impressao", {
            method: "PATCH"
        }, {
            isDownloadFile: true
        })

    if(response && !globalState.pageCertificado.dtHrImpressaoProposta.get())
        globalState.pageCertificado.dtHrImpressaoProposta.set(formatISO(new Date(), { representation: "complete" }))
    
    globalState.isLoading.set(false);
}

export const uploadPropostaAssinada = async (propostaFile: File) => {
    globalState.isLoading.set(true);

    const fd = new FormData();
    fd.append('file', propostaFile);

    const response = await fetchWrapper<{ idAnexo: number }>(
        "/api/certificado/proposta/anexar", {
            method: "POST",
            body: fd
        })

    if (response) {
        const now = formatISO(new Date(), { representation: "complete" });
        const anexos = [...(globalState.pageCertificado.propostasAnexadas.get({ noproxy: true}) ?? []).map(p => p)];

        anexos.push({
            idAnexo: response.idAnexo,
            dtHrAnexo: now,
            nomeArquivo: propostaFile.name
        });
        
        globalState.pageCertificado.merge({
            dtHrPropostaAnexada: now,
            propostasAnexadas: anexos
        });
    }

    globalState.isLoading.set(false);
}

export const downloadPropostaAssinada = async (idAnexo: number) => {
    globalState.isLoading.set(true);

    await fetchWrapper(`/api/certificado/proposta/anexada/${idAnexo}`, 
        null, 
        {
            isDownloadFile: true
        });
    
    globalState.isLoading.set(false);
    
}

export const updatePagamentoStatus = async (showLoaders: boolean = true) => {
    if (showLoaders)
        globalState.isLoading.set(true);

    const response = await fetchWrapper<{ 
        status: string, 
        dtHrPagamento: string, 
        ehEfetivouSigas?: boolean 
    }>("/api/certificado/pay2b/order/status")

    if(response && response.status !== globalState.pageCertificado.statusPagamento?.get()) {
        const toMerge: IPageCertificadoState = {};
        toMerge["statusPagamento"] = response.status;
        toMerge["ehEfetivouSigas"] = response.ehEfetivouSigas;
        
        if (response.dtHrPagamento) 
            toMerge["dtHrPagamento"] = response.dtHrPagamento;

        globalState.pageCertificado.merge(toMerge);
    }

    if (showLoaders)
        globalState.isLoading.set(false);
}

export const downloadFormCancelamento = async (codMotivoCancelamento: string) => {
    globalState.isLoading.set(true);

    await fetchWrapper(`/api/certificado/cancelar/form-cancelamento/${codMotivoCancelamento}`, 
        null, 
        {
            isDownloadFile: true
        });
    
    globalState.isLoading.set(false);
    
}

export const downloadFormCancelamentoAnexado = async () => {
    globalState.isLoading.set(true);

    await fetchWrapper("/api/certificado/cancelar/form-cancelamento/anexado", 
        null, 
        {
            isDownloadFile: true
        });
    
    globalState.isLoading.set(false);
    
}

export const cancelar = async (codMotivoCancelamento: string, formCancelamentoFile?: File) => {
    globalState.isLoading.set(true);

    const fd = new FormData();
    
    fd.append('codMotivoCancelamento', codMotivoCancelamento);

    if (formCancelamentoFile)
        fd.append('file', formCancelamentoFile);

    const response = await fetchWrapper(
        "/api/certificado/cancelar", {
            method: "PATCH",
            body: fd
        })

    if (response) {
        globalState.pageCertificado.merge(
            {
                dtHrCancelamento: formatISO(new Date(), { representation: "complete" }),
                codMotivoCancelamento,
                nomeArquivoFormCancelamento: formCancelamentoFile?.name
            }
        );
    }
    
    globalState.isLoading.set(false);

    return response;
}

export const pagar = async () => {
    globalState.isLoading.set(true);

    const response = await fetchWrapper<{ urlCheckout: string, success: boolean }>(
        "/api/certificado/pagar", {
            method: "POST"
        })

    if (response && response.success) 
        globalState.pageCertificado.urlCheckout.set(response.urlCheckout);
    
    globalState.isLoading.set(false);

    return response;
}

export interface IDadosPagamentoDBC {
    codTipoPagamento?: TipoPagamento
    codBanco?: string
    agencia?: string
    digitoAgencia?: string
    conta?: string
    digitoConta?: string
}

export const definirDadosPagamentoDBC = async (toSave: IDadosPagamentoDBC) => {
    globalState.isLoading.set(true);

    const response = await fetchWrapper<{ success: boolean }>(
        "/api/Certificado/dados-pagamento-dbc", {
            method: "POST",
            body: JSON.stringify(toSave),
            headers: {
                'Content-type': 'application/json; charset=UTF-8'
            }
        })
        
        if (response && response.success) {
            globalState.pageCertificado.merge(toSave);
            globalState.pageCertificado.dtHrDefinicaoDadosBancariosDBC.set(formatISO(new Date(), { representation: "complete" }))
        }
    
    globalState.isLoading.set(false);

    return response;
}

export const downloadPdfCertificado = async () => {
    globalState.isLoading.set(true);

    const response = await fetchWrapper(
        "/api/certificado/impressao", {
            method: "PATCH"
        }, {
            isDownloadFile: true
        })

    if(response)
        globalState.pageCertificado.dtHrEmissaoCertificado.set(formatISO(new Date(), { representation: "complete" }))
    
    globalState.isLoading.set(false);
}

export const efetivarNoSigas = async () => {
    globalState.isLoading.set(true);

    const response = await fetchWrapper<{ ehEfetivouSigas: boolean }>(
        "/api/certificado/sigas/efetivar", {
            method: "PATCH"
        })

    if (response) 
        globalState.pageCertificado.ehEfetivouSigas.set(response.ehEfetivouSigas);
    
    globalState.isLoading.set(false);
}

let pageCertificado = null;

export const gotoPageCertificado = async () => {
    if (!pageCertificado) {
        globalState.isLoading.set(true);

        pageCertificado = await getPageCertificado();

        if (pageCertificado) 
            globalState.pageCertificado.set(pageCertificado);
    }

    if (pageCertificado) {
        globalState.simulacao.passo.set(enumPasso.certificado);
        await router.navigate("/certificado", { replace: true });
    }

    globalState.isLoading.set(false);

}